import {Frequency, LastResult} from '@hconnect/common/types'
import {Card, CardTitle, dataTestId} from '@hconnect/uikit'
import {DateRangePicker} from '@hconnect/uikit/src/lib2/components/momentDatePicker'
import {Box} from '@mui/material'
import moment, {Moment} from 'moment-timezone'
import React, {FC, useState} from 'react'
import {FormProvider, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {useUrlParam} from '../../../../../routing'
import {useSentDateToVerifyFormula} from '../../hooks/useSentDateToVerifyFormula'
import {FunctionDefinition} from '../../types'
import {formatFormula, minifyFormula, parseAndFormatTimeRange} from '../../utils'

import {Config, FormulaFormFields} from './FormulaFormFields'

import {useVerifyKpiFormula} from '@settings/modules/plant-setup/plant-setup-tabs/kpis/hooks/useVerifyKpiFormula'

type FormulaFormProps = {
  functions?: FunctionDefinition[]
  formula: string | null
  kpiId: number
  showLoader?: boolean
  frequency?: Frequency
  lastResult?: LastResult
  onRegisterInsertHandler?: (handler: (fn: string) => void) => void
}

const config: Config<{customFormula?: string}> = {name: 'customFormula'}

export const FormulaFormCard: FC<FormulaFormProps> = ({
  formula,
  kpiId,
  lastResult,
  frequency,
  functions,
  showLoader,
  onRegisterInsertHandler
}) => {
  const plantCode = useUrlParam('plantCode')
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const {t} = useTranslation()
  const {mutateAsync: verifyKpiFormula} = useVerifyKpiFormula()
  const {mutateAsync: sentDateToVerifyFormula, isPending, data} = useSentDateToVerifyFormula()
  const [timeRange, setTimeRange] = useState<[Moment, Moment] | undefined>(
    lastResult && [
      moment.tz(lastResult.startTime, timezone),
      moment.tz(lastResult.endTime, timezone)
    ]
  )
  const [formattedTimeRange, setFormattedTimeRange] = useState<string | undefined>()

  const methods = useForm<{customFormula?: string}>({
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {customFormula: formula ? formatFormula(formula) : undefined}
  })
  const {handleSubmit, control, reset} = methods

  const submit = handleSubmit(async ({customFormula}) => {
    await Promise.all([
      timeRange
        ? sentDateToVerifyFormula({
            plantCode,
            formula: (customFormula && minifyFormula(customFormula)) || null,
            from: timeRange[0].toDate(),
            to: timeRange[1].toDate()
          })
        : Promise.resolve(undefined),
      verifyKpiFormula({
        formula: customFormula && minifyFormula(customFormula),
        kpiId,
        plantCode
      })
    ]).finally(() => {
      reset({customFormula: customFormula && formatFormula(customFormula)})
    })
  })

  return (
    <Card
      headerContent={
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <CardTitle sx={{mb: 0, width: 200}}>
            {t('kpiCalculationSettings.label.formula')}
          </CardTitle>
          <DateRangePicker
            onTimeRangeParamChange={(timeRange: string | undefined) => {
              const {moments, formatted} = parseAndFormatTimeRange(timeRange)
              if (moments) setTimeRange(moments)
              setFormattedTimeRange(formatted[0])
            }}
            timezone={timezone}
            disableCustomSelect={false}
            noQuickSelection
            fullWidth={false}
            isContrastMode={false}
            hideSingleDateSwitch
            isSingleDateDefault
            withForm
            timeRangeParam={timeRange}
          />
        </Box>
      }
      sx={{boxShadow: 'none'}}
      {...dataTestId('formula-card')}
    >
      <>
        <FormProvider {...methods}>
          <form onSubmit={submit} {...dataTestId(`formula-form-${kpiId}`)}>
            <FormulaFormFields
              lastResult={lastResult}
              calculatedValue={
                data !== null && !isNaN(Number(data))
                  ? Number(data).toFixed(2).toString()
                  : lastResult !== undefined
                    ? lastResult.value.toString()
                    : ''
              }
              formattedTimeRange={formattedTimeRange}
              isLoading={isPending || showLoader}
              setTimeRange={setTimeRange}
              functions={functions}
              frequency={frequency}
              config={config}
              control={control}
              showSubmit
              onRegisterInsertHandler={onRegisterInsertHandler}
            />
          </form>
        </FormProvider>
      </>
    </Card>
  )
}
